/**
 * PERSISTANCE DE LA SÉRIE de la visionneuse (audit C1).
 *
 * La série feuilletée (mode « série » / mode TAGUEUR) vit dans `history.state`, que le
 * navigateur JETTE au rechargement. Conséquence coûteuse : une session de tagage de
 * 300 photos interrompue par un F5 retombait en visionneuse classique, SANS AUCUN MOYEN
 * de reprendre la série — il fallait retourner à la galerie, re-sélectionner, relancer.
 *
 * On double donc `history.state` d'une réserve `sessionStorage`, relue quand l'état de
 * route est ABSENT (entrée à froid : F5, lien collé, restauration d'onglet).
 *
 * PATRON REPRIS DE `lib/scroll-restore.ts` : cœur PUR et testable (`Storage` injecté),
 * jamais d'exception (mode privé, quotas, JSON corrompu → au pire, pas de restauration).
 *
 * DEUX DIFFÉRENCES ASSUMÉES avec `scroll-restore` :
 *  - lecture NON destructive : plusieurs F5 d'affilée doivent tous restaurer ;
 *  - la réserve est GARDÉE par l'id demandé (`assetId ∈ series`) plutôt que consommée.
 *    C'est ce garde — et non l'effacement — qui empêche une série périmée de s'appliquer
 *    à une photo ouverte par un autre chemin. Un plafond d'âge évite de ressusciter une
 *    session vieille de plusieurs jours.
 */

/** Clé unique de la réserve (namespace `xphoto.*`, comme le thème / le tri par défaut). */
export const SERIES_SESSION_KEY = 'xphoto.viewer.series';

/** Au-delà, la réserve est considérée périmée (une session de tagage ne dure pas 24 h). */
export const SERIES_SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Série feuilletée + son contexte de retour, tels que re-posés dans l'état de route. */
export interface SeriesSession {
  /** Chemin + query de retour (× / Échap). */
  back: string;
  /** Liste ORDONNÉE d'ids feuilletée. */
  series: string[];
  /** Mode TAGUEUR si présent (sinon visionneuse/série classique). */
  mode?: 'tag';
  /** Horodatage d'écriture (ms epoch), pour le plafond d'âge. */
  savedAt: number;
}

/** Valide une valeur brute relue du storage (jamais de confiance au contenu stocké). */
function parseSession(raw: unknown): SeriesSession | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const candidate = raw as Partial<SeriesSession>;
  if (typeof candidate.back !== 'string') return null;
  if (typeof candidate.savedAt !== 'number' || !Number.isFinite(candidate.savedAt)) return null;
  if (!Array.isArray(candidate.series)) return null;
  if (!candidate.series.every((id): id is string => typeof id === 'string')) return null;
  if (candidate.series.length === 0) return null;
  if (candidate.mode !== undefined && candidate.mode !== 'tag') return null;
  return {
    back: candidate.back,
    series: candidate.series,
    ...(candidate.mode === 'tag' ? { mode: 'tag' as const } : {}),
    savedAt: candidate.savedAt,
  };
}

/** Écrit la réserve (no-op silencieux si le storage est indisponible ou plein). */
export function saveSeriesSession(storage: Storage, session: SeriesSession): void {
  try {
    storage.setItem(SERIES_SESSION_KEY, JSON.stringify(session));
  } catch {
    // Storage plein / indisponible : on abandonne la réserve (dégradation d'origine).
  }
}

/**
 * Relit la réserve pour `assetId`. Rend `null` si : pas de réserve, contenu corrompu,
 * réserve plus vieille que `SERIES_SESSION_MAX_AGE_MS`, ou `assetId` HORS de la série
 * (la réserve ne concerne alors pas cette photo).
 */
export function readSeriesSession(
  storage: Storage,
  assetId: string,
  now: number = Date.now(),
): SeriesSession | null {
  let raw: string | null;
  try {
    raw = storage.getItem(SERIES_SESSION_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  let session: SeriesSession | null;
  try {
    session = parseSession(JSON.parse(raw));
  } catch {
    return null;
  }
  if (session === null) return null;
  if (now - session.savedAt > SERIES_SESSION_MAX_AGE_MS) return null;
  if (!session.series.includes(assetId)) return null;
  return session;
}

/** Accès défensif au `sessionStorage` (null hors navigateur / storage bloqué). */
function sessionStorageOrNull(): Storage | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null;
  }
}

/** Wrapper navigateur de `saveSeriesSession` (no-op hors navigateur). */
export function storeSeriesSession(session: Omit<SeriesSession, 'savedAt'>): void {
  const storage = sessionStorageOrNull();
  if (!storage) return;
  saveSeriesSession(storage, { ...session, savedAt: Date.now() });
}

/** Wrapper navigateur de `readSeriesSession` (null hors navigateur). */
export function loadSeriesSession(assetId: string): SeriesSession | null {
  const storage = sessionStorageOrNull();
  if (!storage) return null;
  return readSeriesSession(storage, assetId);
}
